import React, { useMemo, useState } from 'react';
import { CircleHelp, Map, Plus, Minus } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FramesPanel } from '@/components/canvas/FramesPanel';
import { useCrispChat } from '@/lib/crisp';
import { useDiagramStore } from '@/stores/diagramStore';

type ZoomToolbarWindow = Window & {
  __konvaStage?: {
    width?: () => number;
    height?: () => number;
  };
};

interface ZoomToolbarProps {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomTo: (scale: number) => void;
  onZoomReset: () => void;
  onZoomFit: () => void;
  showNavigationUtilities?: boolean;
  className?: string;
}

export const ZoomToolbar: React.FC<ZoomToolbarProps> = ({
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomTo,
  onZoomReset,
  onZoomFit,
  showNavigationUtilities = false,
  className,
}) => {
  const { t } = useTranslation('diagram');
  const [minimapOpen, setMinimapOpen] = useState(false);
  const stage = useDiagramStore((state) => state.stage);
  const setStage = useDiagramStore((state) => state.setStage);
  const { openChat } = useCrispChat();

  const zoomPresets = useMemo(
    () => [
      { label: '200%', value: 2 },
      { label: '150%', value: 1.5 },
      { label: '125%', value: 1.25 },
      { label: '100% (1:1)', value: 1 },
      { label: '75%', value: 0.75 },
      { label: '50%', value: 0.5 },
      { label: '25%', value: 0.25 },
    ],
    []
  );

  const approxEqual = (a: number, b: number) => Math.abs(a - b) < 0.005;

  const handleMinimapPan = (x: number, y: number) => {
    const stageNode = (window as ZoomToolbarWindow).__konvaStage;
    const stageWidth = typeof stageNode?.width === 'function' ? stageNode.width() : window.innerWidth;
    const stageHeight = typeof stageNode?.height === 'function' ? stageNode.height() : window.innerHeight;
    const viewportWidth = stageWidth / stage.scale;
    const viewportHeight = stageHeight / stage.scale;

    setStage({
      offsetX: -(x - viewportWidth / 2) * stage.scale,
      offsetY: -(y - viewportHeight / 2) * stage.scale,
    });
  };

  return (
    <>
      <div
        data-testid="tid.editor.zoomToolbar"
        className={cn(
          'absolute bottom-6 right-6 z-50 bg-background border rounded-[24px] shadow-lg p-1.5 flex items-center gap-1',
          className
        )}
      >
        <TooltipProvider skipDelayDuration={0} disableHoverableContent>
        {showNavigationUtilities && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <ThemeToggle size="sm" testId="tid.editor.themeToggle" />
                </div>
              </TooltipTrigger>
              <TooltipContent key={t('tools.toggleTheme')}>{t('tools.toggleTheme')}</TooltipContent>
            </Tooltip>
            <div className="relative self-stretch mx-0">
              <div className="absolute inset-y-0 left-0 w-px bg-border -my-1.5" />
            </div>
          </>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="tid.editor.zoom.out" onClick={onZoomOut} aria-label={t('canvas.zoomOut')}>
              <Minus className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent key={t('canvas.zoomOut')}>{t('canvas.zoomOut')}</TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-1.5 min-w-[56px] justify-center text-xs"
                  data-testid="tid.editor.zoom.menu"
                  aria-label={t('zoom.options')}
                >
                  <span>{Math.round(zoom * 100)}%</span>
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent key={t('zoom.options')}>{t('zoom.options')}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="center" className="w-[200px]">
            {zoomPresets.map((preset) => (
              <DropdownMenuItem
                key={preset.value}
                data-testid={`tid.editor.zoom.preset.${Math.round(preset.value * 100)}`}
                onClick={() => {
                  if (approxEqual(preset.value, 1)) {
                    onZoomReset();
                  } else {
                    onZoomTo(preset.value);
                  }
                }}
                className={cn(
                  'flex items-center justify-between',
                  approxEqual(zoom, preset.value) ? 'bg-accent text-accent-foreground' : undefined
                )}
              >
                {preset.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="tid.editor.zoom.fit" onClick={onZoomFit}>{t('zoom.toFit')}</DropdownMenuItem>
            <DropdownMenuItem data-testid="tid.editor.zoom.reset" onClick={onZoomReset}>{t('zoom.resetView')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="tid.editor.zoom.in" onClick={onZoomIn} aria-label={t('canvas.zoomIn')}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent key={t('canvas.zoomIn')}>{t('canvas.zoomIn')}</TooltipContent>
        </Tooltip>

        {showNavigationUtilities && (
          <>
            <div className="relative self-stretch mx-0">
              <div className="absolute inset-y-0 left-0 w-px bg-border -my-1.5" />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  data-testid="tid.editor.help"
                  aria-label={t('tools.help')}
                  onClick={openChat}
                >
                  <CircleHelp className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent key={t('tools.help')}>{t('tools.help')}</TooltipContent>
            </Tooltip>
            <div className="relative self-stretch mx-0">
              <div className="absolute inset-y-0 left-0 w-px bg-border -my-1.5" />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  data-minimap-toggle="true"
                  data-testid="tid.editor.minimap"
                  className={cn('h-7 w-7', minimapOpen ? 'bg-muted' : undefined)}
                  onClick={() => setMinimapOpen((open) => !open)}
                  aria-pressed={minimapOpen}
                  aria-label={t('tools.minimap', { defaultValue: 'Minimap' })}
                >
                  <Map className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent key={t('tools.minimap', { defaultValue: 'Minimap' })}>
                {t('tools.minimap', { defaultValue: 'Minimap' })}
              </TooltipContent>
            </Tooltip>
          </>
        )}
        </TooltipProvider>
      </div>

      {showNavigationUtilities && minimapOpen && (
        <FramesPanel
          readOnly
          onClose={() => setMinimapOpen(false)}
          onPanTo={handleMinimapPan}
        />
      )}
    </>
  );
};
